import React, { useState, useEffect, useCallback } from 'react';
import './RevenueFinder.css';
import SegmentOptimizerChat from './SegmentOptimizerChat';
import CapacityBanner from '../components/Billing/CapacityBanner';
import TrialBanner from '../components/Billing/TrialBanner';
import SegmentCounts from '../components/SegmentCounts/SegmentCounts';
import RevealMore from '../components/RevealMore/RevealMore';
import useBillingQuota from '../components/Billing/useBillingQuota';
import { billingTranslations } from '../components/Billing/billingCopy';
import IntegrationBadges from '../components/IntegrationBadges';
import PlaysMeter from '../components/Plays/PlaysMeter';
import { dedupFetch } from '../utils/dedupFetch';

const BILLING_UI_ENABLED = process.env.REACT_APP_ENABLE_BILLING === 'true';

// Caption bajo el boton "ACTIVATED": muestra por separado la audiencia elegible
// y los perfiles realmente subidos a Klaviyo (pueden diferir: cada cliente entra
// a lo sumo a 2 segmentos UPSELLerate a la vez). Si el backend aun no expuso el
// numero subido (activaciones viejas), cae al comportamiento previo (solo audiencia).
function renderSyncedCaption(act) {
  if (!act) return null;
  const audience = act.audience_count != null ? act.audience_count : act.profiles_sent;
  const uploaded = act.profiles_uploaded;
  if (audience == null) return null;
  const audienceStr = Number(audience).toLocaleString();
  const showBoth = uploaded != null && Number(uploaded) !== Number(audience);
  return (
    <span
      className="rf-synced-caption"
      style={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.3, textAlign: 'center' }}
      title="The audience is everyone eligible with an email. Uploaded profiles can be fewer: each customer joins at most 2 UPSELLerate segments at a time."
    >
      {showBoth
        ? `${audienceStr} in audience · ${Number(uploaded).toLocaleString()} uploaded to Klaviyo`
        : `${audienceStr} synced`}
      {' '}
      <span className="material-symbols-outlined" style={{ fontSize: '12px', verticalAlign: 'text-bottom' }}>info</span>
    </span>
  );
}

export default function RevenueFinder({ companyInfo, token: propToken, onNavigate, onAskAI }) {
  // Authentication & Configuration State
  const [token, setToken] = useState(propToken || '');
  const [selectedCompany, setSelectedCompany] = useState(companyInfo?.company_id || '');
  const [companies, setCompanies] = useState([
    { id: 'default', name: 'DEFAULT' },
    { id: 'tucketts', name: 'Tucketts' },
    { id: 'saldosahuevo', name: 'Saldos a Huevo' }
  ]);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [tempToken, setTempToken] = useState(token);
  const [tempCompany, setTempCompany] = useState(selectedCompany);

  // Layout & Navigation State
  const [activeTab, setActiveTab] = useState('engine');

  // Optimizer Input State
  const [optimizerText, setOptimizerText] = useState('');

  // Levers state (Interactive Cards)
  const [expandedLevers, setExpandedLevers] = useState([]);
  const [leversList, setLeversList] = useState([]);
  const [restLevers, setRestLevers] = useState([]);
  const [weeklyTotals, setWeeklyTotals] = useState(null);
  const [isMoreOppsOpen, setIsMoreOppsOpen] = useState(false);

  // Deep-link highlight: when arriving from the Revenue Checkpoint's metric
  // chat ("View this play in Revenue Engine →"), the lever code to jump to is
  // handed off via sessionStorage (RevenueCheckpoint.jsx sets it, then
  // navigates here). We read it once on mount, scroll to the matching card
  // once the levers/plays data has loaded, and briefly highlight it.
  const [highlightLeverCode, setHighlightLeverCode] = useState(null);

  // AI Panel State (Sidebar Drawer)
  const [isAiPanelOpen, setIsAiPanelOpen] = useState(false);
  const [aiFocus, setAiFocus] = useState(null);

  // Klaviyo activation state
  const [activatingLeverId, setActivatingLeverId] = useState(null);
  const [confirmLever, setConfirmLever] = useState(null); // palanca pendiente de confirmar
  const [confirmAudienceCount, setConfirmAudienceCount] = useState(null); // null=cargando
  const [leverActivations, setLeverActivations] = useState({}); // lever_code -> {status, segment_name}
  const [confirmStopLever, setConfirmStopLever] = useState(null); // palanca a pausar
  // Refresh billing usage when a Play changes activation state. This key only
  // triggers a fresh /billing/entitlements read; it never derives a count.
  const playsMeterRefreshKey = Object.entries(leverActivations || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([code, activation]) => `${code}:${activation?.status || ''}`)
    .join('|');

  // Estados optimistas recientes (lever_code -> {status, ts}). El backend
  // (/levers/activate-klaviyo) responde 'syncing' de inmediato pero escribe la
  // fila en BigQuery EN SEGUNDO PLANO: un fetchActivations justo despues trae
  // el estado VIEJO (sin fila, o 'paused'/'active' stale) y pisaba el estado
  // optimista, soltando el boton como "REVIEW & ACTIVATE" y matando el polling.
  // Mientras una entrada sea reciente (<60s) y el backend no confirme la
  // transicion (o reporte 'failed'), se conserva el estado local.
  const optimisticStatusRef = React.useRef({});
  const OPTIMISTIC_TTL_MS = 240000; // 4 min: cubre writes lentos de BigQuery sin ser fallo real; 'active'/'failed' del backend siempre ganan de inmediato
  const markOptimistic = (leverCode, status) => {
    optimisticStatusRef.current[leverCode] = { status, ts: Date.now() };
  };

  const fetchActivations = React.useCallback(() => {
    if (!propToken || !companyInfo?.company_id) return Promise.resolve();
    // Returns the promise (not fire-and-forget) so callers -- notably
    // handleReviewActivate -- can await the FRESH status before releasing the
    // button's own loading state. Without this, there's a visible gap where
    // activatingLeverId clears (button looks idle/"Activate" again) before
    // leverActivations has the new 'syncing' row, tempting a double click.
    return fetch(
      `https://dashboard-dot-primeval-gear-417120.ue.r.appspot.com/levers/activations?company=${encodeURIComponent(companyInfo.company_id)}`,
      { headers: { 'Authorization': `Bearer ${propToken}` } }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!(d && d.activations)) return;
        setLeverActivations((prev) => {
          const next = { ...d.activations };
          const now = Date.now();
          Object.entries(optimisticStatusRef.current).forEach(([code, entry]) => {
            if (!entry) return;
            if (now - entry.ts > OPTIMISTIC_TTL_MS) {
              delete optimisticStatusRef.current[code];
              return;
            }
            const backendStatus = next[code]?.status;
            const confirmed =
              backendStatus === entry.status ||
              backendStatus === 'failed' ||
              (entry.status === 'syncing' && backendStatus === 'active');
            if (confirmed) {
              delete optimisticStatusRef.current[code];
              return;
            }
            // El backend aun no refleja el cambio (fila vieja o ausente):
            // conservar el estado optimista local, sin perder los demas
            // campos que ya tengamos de esa activacion.
            next[code] = { ...(prev[code] || {}), ...(next[code] || {}), status: entry.status };
          });
          return next;
        });
      })
      .catch(() => {});
  }, [propToken, companyInfo]);

  useEffect(() => { fetchActivations(); }, [fetchActivations]);

  // Mientras alguna palanca este 'syncing', refrescar el estado cada 8s (con
  // tope de intentos) hasta que quede 'active' o 'failed'.
  useEffect(() => {
    const anySyncing = Object.values(leverActivations || {})
      .some((a) => a && a.status === 'syncing');
    if (!anySyncing) return;
    let attempts = 0;
    const id = setInterval(() => {
      attempts += 1;
      fetchActivations();
      if (attempts >= 30) clearInterval(id); // ~4 min max
    }, 8000);
    return () => clearInterval(id);
  }, [leverActivations, fetchActivations]);

  const openActivationConfirmation = (lever) => {
    setConfirmLever(lever);
    setConfirmAudienceCount(null);
    if (!lever?.lever_code) return;
    fetch(
      `https://dashboard-dot-primeval-gear-417120.ue.r.appspot.com/levers/audience-count?company=${encodeURIComponent(companyInfo?.company_id || '')}&lever=${encodeURIComponent(lever.lever_code)}`,
      { headers: { 'Authorization': `Bearer ${propToken}` } }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setConfirmAudienceCount(d ? d.customers_with_email : -1))
      .catch(() => setConfirmAudienceCount(-1));
  };

  const handleStopSync = async (lever) => {
    if (!lever?.lever_code) return;
    setConfirmStopLever(null);
    setActivatingLeverId(lever.id);
    try {
      const res = await fetch(
        `https://dashboard-dot-primeval-gear-417120.ue.r.appspot.com/levers/deactivate-klaviyo?company=${encodeURIComponent(companyInfo?.company_id || '')}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${propToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ lever: lever.lever_code }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data && data.detail) || `Error ${res.status}`);
      // Reflejar la pausa enseguida. No esperamos el siguiente refresh para que
      // el boton no vuelva a verse como ACTIVATED despues de confirmar.
      markOptimistic(lever.lever_code, 'paused');
      setLeverActivations((current) => ({
        ...current,
        [lever.lever_code]: {
          ...(current[lever.lever_code] || {}),
          status: 'paused',
          segment_name: data.segment_name || current[lever.lever_code]?.segment_name,
        },
      }));
      setToastMessage(`Weekly sync paused for "${data.segment_name}" — the Klaviyo segment will empty out.`);
    } catch (err) {
      setToastMessage(`Could not pause sync: ${err.message}`);
    } finally {
      setActivatingLeverId(null);
      fetchActivations();
      setShowToast(true);
      setTimeout(() => setShowToast(false), 6000);
    }
  };

  // Billing quota (Active Revenue Opportunities). Fail-open: if it can't load,
  // canActivate stays true and nothing is blocked. See useBillingQuota.
  const {
    quotaTotal,
    activeCount,
    remainingSlots,
    canActivate,
    atCapacity,
    hasQuotaData,
    refresh: refreshQuota,
  } = useBillingQuota({ token: propToken });
  const capacityCopy = billingTranslations.en.capacity;

  // Segment Optimizer Chat State (multi-turn, /segment-chat)
  const [isSegmentChatOpen, setIsSegmentChatOpen] = useState(false);
  const [segmentChatContext, setSegmentChatContext] = useState(null);
  const [segmentChatInitialMessage, setSegmentChatInitialMessage] = useState(null);
  // Bumped to remount the chat and start a fresh session.
  const [segmentChatKey, setSegmentChatKey] = useState(0);

  // Recommended Actions State
  const [actionsList, setActionsList] = useState([]);

  // Impact states (Performance snapshot)
  const [snapshotRecovered, setSnapshotRecovered] = useState('');
  const [snapshotGoal, setSnapshotGoal] = useState('');
  const [snapshotPct, setSnapshotPct] = useState(0);
  const [snapshotTrend, setSnapshotTrend] = useState('↑ $340 vs last month');
  const [playsList, setPlaysList] = useState([
    {
      id: 'play-1',
      label: 'SECOND PURCHASE',
      title: '$340 recovered of $520 projected',
      recoveredText: '65% hit rate',
      isActive: false
    },
    {
      id: 'play-2',
      label: 'CHURN AVOIDANCE',
      title: '$890 recovered of $1,100 projected',
      recoveredText: '65% hit rate',
      isActive: false
    },
    {
      id: 'play-3',
      label: 'ALS CROSS-SELL',
      title: '$1,280 projected · 14 days remaining',
      recoveredText: '',
      isActive: true
    }
  ]);
  const [totalRecovered, setTotalRecovered] = useState('');
  const [heroRange, setHeroRange] = useState('');

  // Sticky footer activation notification
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Backfill status from summary endpoint
  const [backfillStatus, setBackfillStatus] = useState(null);

  // Read the deep-link handoff once on mount. sessionStorage (not a query
  // param) was chosen to match the existing RevenueCheckpoint.jsx handoff —
  // consumed once and cleared so a later manual visit to this page doesn't
  // re-trigger the highlight.
  useEffect(() => {
    try {
      const code = sessionStorage.getItem('rf_highlight_lever');
      if (code) {
        sessionStorage.removeItem('rf_highlight_lever');
        setHighlightLeverCode(code);
      }
    } catch (err) {
      // sessionStorage unavailable (e.g. private browsing) — no highlight, no crash.
    }
  }, []);

  // Fetch companies on mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await fetch('https://dashboard-dot-primeval-gear-417120.ue.r.appspot.com/api/dashboard/companies');
        if (res.ok) {
          const data = await res.json();
          if (data && data.companies) {
            setCompanies(data.companies);
          }
        }
      } catch (err) {
        console.warn('Failed to load companies, using preloads:', err);
      }
    };
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (!propToken || !companyInfo?.company_id) return;

    const company = companyInfo.company_id;
    const headers = { 'Authorization': `Bearer ${propToken}` };
    const baseUrl = 'https://dashboard-dot-primeval-gear-417120.ue.r.appspot.com';

    const fetchRealData = async () => {
      try {
        // SUMMARY
        // dedupFetch: App.js (routing de onboarding + resolucion de nombre) y
        // RevenueCheckpoint ya piden este mismo endpoint al entrar.
        const summaryRes = await dedupFetch(
          `${baseUrl}/revenue-finder/summary?company=${company}`,
          { headers }
        );
        if (summaryRes.ok) {
          const summaryData = await summaryRes.json();
          if (summaryData.full_backfill_status) {
            setBackfillStatus(summaryData.full_backfill_status);
          }
          const metrics = summaryData.mainMetrics;
          if (metrics) {
            if (metrics.currentMonthRevenue != null) {
              setSnapshotRecovered('$' + Math.round(
                metrics.currentMonthRevenue).toLocaleString());
            }
            const goal = metrics.monthlyRevenueGoal || metrics.projection;
            if (goal != null && goal > 0) {
              setSnapshotGoal('$' + Math.round(goal).toLocaleString());
              const pct = Math.round(
                (metrics.currentMonthRevenue / goal) * 100);
              setSnapshotPct(Math.min(Math.max(pct, 0), 100));
            }
          }
        }

        // OPPORTUNITIES
        const oppsRes = await fetch(
          `${baseUrl}/revenue-finder/opportunities?company=${company}&week=current`,
          { headers }
        );
        if (oppsRes.ok) {
          const oppsData = await oppsRes.json();
          if (Array.isArray(oppsData.weeklyOpportunities)) {
            const totalImpact = oppsData.weeklyOpportunities.reduce(
              (sum, o) => sum + (Number(o.impact) || 0), 0);
            if (totalImpact > 0) {
              const lo = Math.round(totalImpact * 0.75 / 1000) * 1000;
              const hi = Math.round(totalImpact / 1000) * 1000;
              setHeroRange('$' + lo.toLocaleString() + ' – $' + hi.toLocaleString());
            }
            const mappedOpps = oppsData.weeklyOpportunities.map((o, idx) => ({
              id: o.id || (idx + 1),
              lever_code: typeof o.id === 'string' ? o.id : null,
              lever_type: o.lever_type || '',
              title: o.title || o.lever_type || 'Revenue Opportunity',
              value: (o.impact != null && o.impact > 0)
                ? '+$' + Math.round(o.impact).toLocaleString()
                : '+$0',
              priority: o.impact > 50000 ? 'HIGH PRIORITY'
                : o.impact > 5000 ? 'HIGH PRIORITY'
                : 'MEDIUM',
              description: o.description || 'Opportunity identified.',
              whyNow: o.why_now || 'Act this week.',
              segment: o.segment || 'Customer segment'
            }));
            setLeversList(mappedOpps);
            if (mappedOpps.length > 0) setAiFocus(mappedOpps[0]);
          }
        }

        // PLAYS
        const playsRes = await fetch(
          `${baseUrl}/revenue-finder/plays?company=${company}`,
          { headers }
        );
        if (playsRes.ok) {
          const playsData = await playsRes.json();
          if (Array.isArray(playsData.revenuePlays)) {
            const mappedPlays = playsData.revenuePlays.map((p, idx) => ({
              id: p.id || `play-${idx}`,
              label: p.lever_type || p.label || p.name || 'ACTIVE PLAY',
              title: p.recovered
                ? '$' + Math.round(p.recovered).toLocaleString()
                  + ' recovered of $'
                  + Math.round(p.projected || 0).toLocaleString()
                  + ' projected'
                : p.title || 'In progress...',
              recoveredText: p.hit_rate || p.status_detail || '',
              isActive: p.is_active || p.status === 'active'
            }));
            setPlaysList(mappedPlays);

            // Revenue Opportunities cards: top4 as big cards, the rest as an
            // expandable "See more" list. Retrocompatible: if no play carries
            // top4===true (older backend), treat everything as top4/big cards
            // and skip the "see more" affordance + weekly totals header.
            const rawPlays = playsData.revenuePlays;
            const anyTop4 = rawPlays.some((p) => p.top4 === true);

            const toCardLever = (p, idx) => ({
              id: p.id || p.lever || `play-${idx}`,
              lever_code: p.lever || (typeof p.id === 'string' ? p.id : null),
              lever_type: p.lever || '',
              title: p.title || 'Revenue Opportunity',
              value: (p.revenueOpportunity != null && p.revenueOpportunity > 0)
                ? '+$' + Math.round(p.revenueOpportunity).toLocaleString()
                : '+$0',
              revenueOpportunity: p.revenueOpportunity || 0,
              customers: p.customers,
              priority: Number(p.priority) === 1 ? 'CRITICAL'
                : (p.revenueOpportunity > 5000 ? 'HIGH PRIORITY' : 'MEDIUM'),
              description: p.description || p.audienceSummary || 'Opportunity identified.',
              whyNow: p.momento || 'Act this week.',
              segment: p.audienceSummary || 'Customer segment',
              conversionRate: p.conversionRate,
              expectedConverters: p.expectedConverters,
              recommendedCategories: Array.isArray(p.recommendedCategories) ? p.recommendedCategories : []
            });

            const topPlays = anyTop4 ? rawPlays.filter((p) => p.top4 === true) : rawPlays;
            const otherPlays = anyTop4 ? rawPlays.filter((p) => p.top4 !== true) : [];

            setLeversList(topPlays.map(toCardLever));
            if (topPlays.length > 0) setAiFocus((prev) => prev || toCardLever(topPlays[0], 0));
            setRestLevers(otherPlays.map(toCardLever));

            if (anyTop4 && playsData.weeklyTotals) {
              setWeeklyTotals(playsData.weeklyTotals);
            } else {
              setWeeklyTotals(null);
            }
          }
        }
      } catch (err) {
        console.warn('Revenue Engine fetch failed:', err);
      }
    };

    fetchRealData();
  }, [propToken, companyInfo]);

  // Once the play cards have loaded, scroll to and briefly highlight the
  // card matching highlightLeverCode. If the target lever only exists among
  // the "more opportunities" (non-top4) plays, auto-expand that section
  // first so the card is actually in the DOM to scroll to.
  useEffect(() => {
    if (!highlightLeverCode) return;
    if (leversList.length === 0 && restLevers.length === 0) return;

    const inTop4 = leversList.some((l) => l.lever_code === highlightLeverCode);
    const inRest = restLevers.some((l) => l.lever_code === highlightLeverCode);
    if (!inTop4 && !inRest) {
      // Nothing matches this lever code (e.g. stale data, or the metric
      // doesn't map to a play this week) — clear silently, no crash.
      setHighlightLeverCode(null);
      return;
    }
    if (inRest && !inTop4) setIsMoreOppsOpen(true);

    // Wait a tick for the (possibly newly-expanded) card to be in the DOM.
    const timer = setTimeout(() => {
      const el = document.querySelector(
        `[data-lever-code="${window.CSS && window.CSS.escape ? window.CSS.escape(highlightLeverCode) : highlightLeverCode}"]`
      );
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('rf-lever-card-highlighted');
        setTimeout(() => el.classList.remove('rf-lever-card-highlighted'), 2500);
      }
      setHighlightLeverCode(null);
    }, 150);
    return () => clearTimeout(timer);
  }, [highlightLeverCode, leversList, restLevers]);

  const toggleLeverDetails = (id) => {
    if (expandedLevers.includes(id)) {
      setExpandedLevers(expandedLevers.filter(item => item !== id));
    } else {
      setExpandedLevers([...expandedLevers, id]);
    }
  };

  const handleApplyFastTag = (tagText) => {
    setOptimizerText(tagText);
  };

  // Descarga la audiencia de la palanca en CSV (mismos datos que se sincronizan)
  const [downloadingLever, setDownloadingLever] = useState(false);
  const handleDownloadLeverCsv = async (lever) => {
    if (!lever?.lever_code || downloadingLever) return;
    setDownloadingLever(true);
    try {
      const res = await fetch(
        `https://dashboard-dot-primeval-gear-417120.ue.r.appspot.com/levers/export-csv?company=${encodeURIComponent(companyInfo?.company_id || '')}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${propToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ lever: lever.lever_code }),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data && data.detail) || `Error ${res.status}`);
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${lever.lever_code.toLowerCase()}_audience.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setToastMessage(`Could not download CSV: ${err.message}`);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 5000);
    } finally {
      setDownloadingLever(false);
    }
  };

  // Activa el segmento de una palanca en Klaviyo (perfiles + segmento dinámico).
  // Nunca crea ni envía campañas.
  const handleReviewActivate = async (lever) => {
    if (!lever?.lever_code) {
      setToastMessage('This lever cannot be activated automatically yet.');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
      return;
    }
    if (activatingLeverId) return;
    setConfirmLever(null);
    setActivatingLeverId(lever.id);
    try {
      const res = await fetch(
        `https://dashboard-dot-primeval-gear-417120.ue.r.appspot.com/levers/activate-klaviyo?company=${encodeURIComponent(companyInfo?.company_id || '')}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${propToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ lever: lever.lever_code, source: 'lever_button' }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((data && data.detail) || `Error ${res.status}`);
      }
      // El backend responde status='syncing' de inmediato pero escribe la fila
      // en BigQuery en segundo plano: pintar 'syncing' OPTIMISTA aqui mismo,
      // sin depender de fetchActivations (que puede traer el estado viejo).
      // Esto ademas dispara el polling de 8s (useEffect sobre leverActivations)
      // que lleva el flujo normal syncing -> active/failed.
      markOptimistic(lever.lever_code, 'syncing');
      setLeverActivations((prev) => ({
        ...prev,
        [lever.lever_code]: {
          ...(prev[lever.lever_code] || {}),
          status: 'syncing',
          segment_name: data.segment_name || prev[lever.lever_code]?.segment_name,
        },
      }));
      const audienceCount = Number(lever.customers);
      setToastMessage(
        Number.isFinite(audienceCount) && audienceCount > 0
          ? `Syncing "${data.segment_name}" to Klaviyo — uploading ${audienceCount.toLocaleString()} profiles. This can take a couple of minutes.`
          : `Syncing "${data.segment_name}" to Klaviyo… this can take a couple of minutes.`
      );
      // A slot may have just been consumed — refresh the quota so the counter
      // and any lock state reflect it.
      refreshQuota();
    } catch (err) {
      setToastMessage(`Could not activate: ${err.message}`);
    } finally {
      // Esperar el estado real ANTES de soltar el boton: si limpiaramos
      // activatingLeverId primero, hay un hueco de varios segundos donde el
      // boton vuelve a verse como "REVIEW & ACTIVATE" (leverActivations
      // todavia no tiene la fila 'syncing') y el cliente puede pensar que el
      // click no funciono y volver a hacer click. Bug reportado 2026-08-08.
      await fetchActivations();
      setActivatingLeverId(null);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 6000);
    }
  };

  // "FIND MY AUDIENCE" ya no llama al API ni muestra un resultado sin acción
  // posible: abre el mismo Segment Optimizer Chat (con activar/sincronizar a
  // Klaviyo/descargar CSV) precargado con el texto del textarea como primer
  // mensaje, para no duplicar esa lógica en dos lugares.
  const handleFindAudience = () => {
    if (!optimizerText.trim()) return;
    openSegmentChat(null, optimizerText);
  };

  // Open the Segment Optimizer chat. Pass a lever to preload its context,
  // or null to start a blank session. `initialMessage` (optional) is sent
  // automatically as the user's first message — used by the "Segment
  // Optimizer" box so its textarea/quick-tag text flows into the same chat
  // that has activate/sync/CSV, instead of a separate dead-end result box.
  // Each open starts a fresh session (key bump).
  const openSegmentChat = (lever, initialMessage) => {
    if (lever) {
      setSegmentChatContext({
        lever_name: lever.title,
        lever_code: lever.lever_code || undefined,
        lever_type: lever.lever_type || undefined,
        target_segment: lever.segment,
        revenue_opportunity: lever.value,
        description: lever.description || undefined,
        why_now: lever.whyNow || undefined,
        priority: lever.priority || undefined,
      });
    } else {
      setSegmentChatContext(null);
    }
    setSegmentChatInitialMessage(initialMessage || null);
    setSegmentChatKey((k) => k + 1);
    setIsSegmentChatOpen(true);
  };

  const openAiPanel = (lever) => {
    setAiFocus(lever);
    setIsAiPanelOpen(true);
  };

  // Called by SegmentOptimizerChat (via onGoToLever) when the user clicks
  // "Go to this lever →" on a recommended-lever card. The chat component
  // itself already calls onClose() right after this, so we do NOT close
  // anything here. leverId is the backend-stable lever code (e.g.
  // "NEW_LOST_WINBACK"), matched against lever.lever_code.
  //
  // Reuses the existing deep-link highlight mechanism (highlightLeverCode +
  // the effect below) instead of a second scroll/highlight implementation —
  // that effect already handles expanding "more opportunities" when the
  // target lever isn't in the top4, scrolling, and flashing the card via the
  // data-lever-code attribute + rf-lever-card-highlighted class.
  const handleGoToLever = useCallback((leverId) => {
    if (!leverId) return;
    setHighlightLeverCode(leverId);
  }, []);

  const handleSaveSettings = () => {
    localStorage.setItem('revenue_finder_token', tempToken);
    localStorage.setItem('revenue_finder_company', tempCompany);
    setToken(tempToken);
    setSelectedCompany(tempCompany);
    setIsSettingsOpen(false);

    setToastMessage('Settings successfully saved to local state!');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleActivateStickyPlay = () => {
    setToastMessage("Activating 'First-time Buyer' sync pipeline across ESP logs...");
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleLogout = () => {
    localStorage.removeItem('revenue_finder_token');
    localStorage.removeItem('revenue_finder_company');
    setToken('');
    setSelectedCompany('default');

    setToastMessage('Logged out successfully.');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const rfLoadingNotice = (() => {
    if (!backfillStatus) return null;
    if (backfillStatus === 'queued' || backfillStatus === 'running' || backfillStatus === 'unknown')
      return 'Preparing revenue opportunities…';
    if (backfillStatus === 'complete' && leversList.length === 0)
      return 'Calculating revenue opportunities…';
    return null;
  })();

  return (
    <div className="rf-body">
      {/* Toast Notification */}
      {showToast && (
        <div
          style={{
            position: 'fixed',
            top: '24px',
            right: '24px',
            backgroundColor: '#121C2A',
            color: '#ffffff',
            padding: '16px 24px',
            borderRadius: '4px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            fontSize: '14px',
            fontWeight: 650,
            borderLeft: '4px solid var(--color-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}
        >
          <span className="material-symbols-outlined" style={{ color: 'var(--color-primary)' }}>info</span>
          {toastMessage}
        </div>
      )}

      {/* Main Container */}
      <main className="rf-main-wrapper">
        {/* Header */}
        <header className="rf-header">
          <div className="rf-header-content">
            <IntegrationBadges companyInfo={companyInfo} token={token} />
            <h2 className="rf-header-title">Revenue Engine</h2>
            <p className="rf-header-subtitle">Your weekly revenue command center — opportunities found in your own customer data.</p>
            {companyInfo?.company_name && (
              <p style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '12px',
                fontWeight: 600,
                color: '#6B2EFF',
                marginTop: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em'
              }}>
                {companyInfo.company_name}
              </p>
            )}
            <TrialBanner token={propToken} />
          </div>
        </header>

        {/* Content Area */}
        <div className="rf-content-container">
          {rfLoadingNotice && (
            <div className="checkpoint-loading-notice">
              <span className="loading-spinner" /> {rfLoadingNotice}
            </div>
          )}

          {/* SECTION 1 — HERO */}
          <section className="rf-hero-redefined">
            <h1 className="rf-hero-amount">{heroRange}</h1>
            <p className="rf-hero-line1">in recoverable revenue this week</p>
            <p className="rf-hero-line2">
              {(() => {
                const playCount = weeklyTotals && weeklyTotals.playCount != null
                  ? weeklyTotals.playCount
                  : (leversList.length + restLevers.length);
                return playCount > 0
                  ? `${playCount} opportunit${playCount === 1 ? 'y' : 'ies'} found this week · updated every Monday`
                  : 'Updated every Monday';
              })()}
            </p>
          </section>

          {/* SECTION 2 — REVENUE LEVERS */}
          <section style={{ width: '100%', marginBottom: '48px' }}>
            <div className="rf-sec-divider-row">
              <span className="rf-sec-divider-label">REVENUE LEVERS</span>
              <div className="rf-sec-divider-rule"></div>
            </div>

            {BILLING_UI_ENABLED && propToken && (
              <PlaysMeter
                token={propToken}
                refreshKey={playsMeterRefreshKey}
                onNavigateToPricing={() => onNavigate && onNavigate('pricing')}
              />
            )}

            {/* Weekly-totals + quota lines removed (2026-08-01, decision Alejo):
                the hero above already tells the same story ($X recoverable +
                N opportunities). Capacity still surfaces via CapacityBanner
                below when at the cap, and via locked buttons per card. */}

            {/* At the cap: never hide the extra opportunities — show the banner
                explaining the limit and how to lift it (upgrade / free a slot). */}
            {atCapacity && (
              <CapacityBanner
                className="rf-capacity-banner"
                onViewPlans={() => onNavigate && onNavigate('pricing')}
              />
            )}

            <div className={leversList.length === 4 ? "rf-levers-redefined-grid rf-levers-grid-2x2" : "rf-levers-redefined-grid"}>
              {leversList.map((lever) => {
                const isExpanded = expandedLevers.includes(lever.id);
                // An already-active lever stays activatable/re-syncable; only
                // NOT-yet-active levers get locked when at capacity. We don't
                // have per-lever active flags here, so gate on canActivate: at
                // the cap every new activation is blocked (grey), value stays
                // fully visible. Fail-open: canActivate is true unless billing
                // explicitly said the account is at its limit.
                // EXCEPTION: a 'paused' play already consumed its quota this
                // billing cycle — resuming is free (backend is idempotent per
                // lever key), so paused levers are never capacity-locked.
                const isPaused = leverActivations[lever.lever_code]?.status === 'paused';
                const activationLocked = !canActivate && !isPaused;

                let cardClass = "rf-lever-card-redefined";
                let badgeClass = "rf-lever-priority-label";
                let numClass = "rf-lever-number";

                if (lever.priority === 'HIGH PRIORITY') {
                  cardClass += " rf-lever-card-high";
                  badgeClass += " rf-priority-high";
                  numClass += " rf-lever-number-high";
                } else if (lever.priority === 'CRITICAL') {
                  cardClass += " rf-lever-card-critical";
                  badgeClass += " rf-priority-critical";
                  numClass += " rf-lever-number-critical";
                } else {
                  cardClass += " rf-lever-card-medium";
                  badgeClass += " rf-priority-medium";
                  numClass += " rf-lever-number-medium";
                }

                return (
                  <div key={lever.id} className={cardClass} data-lever-code={lever.lever_code || lever.id}>

                    {/* Corner "+" toggle removed (2026-08-01): "See details →"
                        below is the single details control per card. */}
                    <span className={badgeClass}>{lever.priority}</span>
                    <h2 className={numClass}>
                      {lever.value}
                      <span
                        className="rf-lever-per-play-tag"
                        title="Estimated return from running this play once on this audience — based on each customer's expected value and purchase history."
                      >
                        est. per play
                      </span>
                    </h2>
                    {lever.lever_type === 'LOST_RECOVERY' && (
                      <p className="rf-lever-context-note">Est. recovery potential · 30-month base</p>
                    )}
                    {lever.customers != null && lever.expectedConverters != null && (() => {
                      const rate = Number(lever.conversionRate) || 0;
                      const pct = rate > 1 ? Math.round(rate) : Math.round(rate * 100);
                      return (
                        <p className="rf-lever-breakdown-text">
                          {Number(lever.customers).toLocaleString()} customers × ~{pct}% expected conversion ≈ {Number(lever.expectedConverters).toLocaleString()} customers
                        </p>
                      );
                    })()}
                    <div className="rf-lever-thin-divider"></div>
                    <h4 className="rf-lever-title-text">{lever.title}</h4>
                    <p className="rf-lever-desc-text">
                      {lever.description
                        ? lever.description.split('Products to feature:')[0].trim()
                        : lever.description}
                    </p>
                    {lever.recommendedCategories && lever.recommendedCategories.length > 0 && (
                      <div className="rf-lever-category-chips">
                        {lever.recommendedCategories.slice(0, 3).map((cat, ci) => (
                          <span key={ci} className="rf-lever-category-chip">{cat}</span>
                        ))}
                      </div>
                    )}

                    <a
                      className="rf-details-link"
                      onClick={() => toggleLeverDetails(lever.id)}
                    >
                      {isExpanded ? 'Hide details ←' : 'See details →'}
                    </a>

                    {isExpanded && (
                      <div className="rf-expanded-block">
                        <div className="rf-expanded-row">
                          <span className="rf-expanded-row-label">WHY NOW</span>
                          <span className="rf-expanded-row-val">{lever.whyNow}</span>
                        </div>
                        <div className="rf-expanded-row">
                          <span className="rf-expanded-row-label">SEGMENT</span>
                          <span className="rf-expanded-row-val">{lever.segment}</span>
                        </div>

                        <div className="rf-ai-bar">
                          <span className="rf-ai-bar-label">AI can draft this segment</span>
                          {/* Single AI entry point per card (2026-08-01): opens the
                              multi-turn Segment Optimizer chat with this lever's
                              context (was openAiPanel, the older one-shot drawer). */}
                          <button
                            className="rf-ai-bar-btn"
                            onClick={() => openSegmentChat(lever)}
                          >
                            Ask AI →
                          </button>
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                      {leverActivations[lever.lever_code]?.status === 'syncing' ? (
                        <button
                          className="rf-btn-review-activate-card rf-btn-activated"
                          style={{ flex: 1 }}
                          title="Uploading the audience to Klaviyo…"
                          disabled
                        >
                          <span className="material-symbols-outlined rf-play-dot-pulsing" style={{ fontSize: '16px', verticalAlign: 'text-bottom', marginRight: '6px' }}>sync</span>
                          Syncing…
                        </button>
                      ) : leverActivations[lever.lever_code]?.status === 'failed' ? (
                        activatingLeverId === lever.id ? (
                          <button
                            className="rf-btn-review-activate-card rf-btn-activate-locked"
                            style={{ flex: 1 }}
                            title="Retrying…"
                            disabled
                          >
                            ACTIVATING…
                          </button>
                        ) : (
                          <button
                            className="rf-btn-review-activate-card rf-btn-sync-failed"
                            style={{ flex: 1 }}
                            title="Sync failed — try again"
                            onClick={() => setConfirmLever(lever)}
                          >
                            Sync failed — try again
                          </button>
                        )
                      ) : leverActivations[lever.lever_code]?.status === 'active' ? (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <button
                            className="rf-btn-review-activate-card rf-btn-activated"
                            style={{ width: '100%' }}
                            title="Synced weekly with Klaviyo — click to stop"
                            disabled={activatingLeverId === lever.id}
                            onClick={() => setConfirmStopLever(lever)}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'text-bottom', marginRight: '6px' }}>sync</span>
                            {activatingLeverId === lever.id ? 'PAUSING…' : 'ACTIVATED'}
                          </button>
                          {renderSyncedCaption(leverActivations[lever.lever_code])}
                        </div>
                      ) : activationLocked ? (
                        <button
                          className="rf-btn-review-activate-card rf-btn-activate-locked"
                          style={{ flex: 1 }}
                          disabled
                          title={capacityCopy.lockedTooltip}
                          aria-label={capacityCopy.lockedTooltip}
                        >
                          {capacityCopy.lockedTooltip}
                        </button>
                      ) : (
                        <button
                          className={activatingLeverId === lever.id ? "rf-btn-review-activate-card rf-btn-activate-locked" : "rf-btn-review-activate-card"}
                          style={{ flex: 1 }}
                          disabled={activatingLeverId === lever.id}
                          onClick={() => openActivationConfirmation(lever)}
                        >
                          {activatingLeverId === lever.id ? 'ACTIVATING…' : 'REVIEW & ACTIVATE'}
                        </button>
                      )}
                      {activationLocked && (
                        <button
                          className="rf-btn-review-activate-card rf-btn-upgrade-inline"
                          title={capacityCopy.lockedTooltip}
                          onClick={() => onNavigate && onNavigate('pricing')}
                        >
                          {capacityCopy.lockedCta}
                        </button>
                      )}
                      {/* AI robot icon removed (2026-08-01): the AI entry now
                          lives inside "See details" (Ask AI bar) — one primary
                          action per card. */}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* "See more opportunities" — compact rows for plays that didn't
                make the top4. Only shown when the backend sent per-play top4
                flags (weeklyTotals/restLevers present); older payloads render
                everything as big cards above and skip this entirely. */}
            {restLevers.length > 0 && (
              <div className="rf-more-opps-wrap">
                <RevealMore
                  expanded={isMoreOppsOpen}
                  onToggle={() => setIsMoreOppsOpen((v) => !v)}
                  panelId="rf-more-opps-panel"
                  label={
                    weeklyTotals && weeklyTotals.restTotal != null ? (
                      <>
                        <span className="reveal-more-num">{restLevers.length}</span> more plays ·{' '}
                        <span className="reveal-more-num">
                          ${Math.round(weeklyTotals.restTotal).toLocaleString()}
                        </span>{' '}
                        more this week
                      </>
                    ) : (
                      <>
                        <span className="reveal-more-num">{restLevers.length}</span> more plays
                      </>
                    )
                  }
                >
                  <div className="rf-more-opps-grid">
                    {restLevers.map((lever) => {
                      const isMiniExpanded = expandedLevers.includes(lever.id);
                      let miniBadgeClass = "rf-more-opps-minicard-badge";
                      if (lever.priority === 'HIGH PRIORITY') {
                        miniBadgeClass += " rf-priority-high";
                      } else if (lever.priority === 'CRITICAL') {
                        miniBadgeClass += " rf-priority-critical";
                      } else {
                        miniBadgeClass += " rf-priority-medium";
                      }
                      return (
                      <div key={lever.id} className="rf-more-opps-minicard" data-lever-code={lever.lever_code || lever.id}>

                        <div className="rf-more-opps-minicard-main">
                          <div className="rf-more-opps-minicard-top-row">
                            {lever.priority ? (
                              <span className={miniBadgeClass}>{lever.priority}</span>
                            ) : null}
                            <button
                              type="button"
                              className="rf-more-opps-minicard-toggle"
                              onClick={() => toggleLeverDetails(lever.id)}
                              aria-label={isMiniExpanded ? "Collapse Details" : "Expand Details"}
                            >
                              {isMiniExpanded ? '−' : '+'}
                            </button>
                          </div>
                          {lever.value ? (
                            <span className="rf-more-opps-minicard-value">
                              {lever.value}
                              <span
                                className="rf-lever-per-play-tag rf-lever-per-play-tag-mini"
                                title="Estimated return from running this play once on this audience — based on each customer's expected value and purchase history."
                              >
                                est. per play
                              </span>
                            </span>
                          ) : null}
                          {lever.lever_type === 'LOST_RECOVERY' && (
                            <p className="rf-lever-context-note">Est. recovery potential · 30-month base</p>
                          )}
                          <span className="rf-more-opps-minicard-title">{lever.title}</span>
                          <p className={isMiniExpanded ? "rf-more-opps-minicard-desc" : "rf-more-opps-minicard-desc rf-more-opps-minicard-desc-clamped"} title={lever.description}>
                            {lever.description}
                          </p>
                          {lever.customers != null ? (
                            <span className="rf-more-opps-minicard-meta">
                              {Number(lever.customers).toLocaleString()} customers
                            </span>
                          ) : null}

                          {isMiniExpanded && (
                            <div className="rf-more-opps-minicard-expanded">
                              <div className="rf-expanded-row">
                                <span className="rf-expanded-row-label">WHY NOW</span>
                                <span className="rf-expanded-row-val">{lever.whyNow}</span>
                              </div>
                              <div className="rf-expanded-row">
                                <span className="rf-expanded-row-label">SEGMENT</span>
                                <span className="rf-expanded-row-val">{lever.segment}</span>
                              </div>
                              <div className="rf-ai-bar">
                                <span className="rf-ai-bar-label">AI can draft this segment</span>
                                <button
                                  className="rf-ai-bar-btn"
                                  onClick={() => openSegmentChat(lever)}
                                >
                                  Ask AI →
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="rf-more-opps-minicard-actions">
                        {leverActivations[lever.lever_code]?.status === 'syncing' ? (
                        <button className="rf-btn-review-activate-card rf-btn-activated" style={{ flex: 1 }} title="Uploading the audience to Klaviyo…" disabled>
                          <span className="material-symbols-outlined rf-play-dot-pulsing" style={{ fontSize: '16px', verticalAlign: 'text-bottom', marginRight: '6px' }}>sync</span>
                          Syncing…
                        </button>
                        ) : leverActivations[lever.lever_code]?.status === 'failed' ? (
                        activatingLeverId === lever.id ? (
                        <button className="rf-btn-review-activate-card rf-btn-activate-locked" style={{ flex: 1 }} title="Retrying…" disabled>
                          ACTIVATING…
                        </button>
                        ) : (
                        <button className="rf-btn-review-activate-card rf-btn-sync-failed" style={{ flex: 1 }} title="Sync failed — try again" onClick={() => setConfirmLever(lever)}>
                          Sync failed — try again
                        </button>
                        )
                        ) : leverActivations[lever.lever_code]?.status === 'active' ? (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          <button
                            type="button"
                            className="rf-more-opps-row-btn rf-btn-activated"
                            title="Synced weekly with Klaviyo — click to stop"
                            disabled={activatingLeverId === lever.id}
                            onClick={() => setConfirmStopLever(lever)}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'text-bottom', marginRight: '4px' }}>sync</span>
                            {activatingLeverId === lever.id ? 'PAUSING…' : 'ACTIVATED'}
                          </button>
                          {renderSyncedCaption(leverActivations[lever.lever_code])}
                        </div>
                        ) : (
                        <button
                          type="button"
                          className={activatingLeverId === lever.id ? "rf-more-opps-row-btn rf-btn-activate-locked" : "rf-more-opps-row-btn"}
                          /* Paused play = quota already consumed this cycle;
                             resuming is free, so never capacity-lock it. */
                          disabled={(!canActivate && leverActivations[lever.lever_code]?.status !== 'paused') || activatingLeverId === lever.id}
                          onClick={() => openActivationConfirmation(lever)}
                          title={!canActivate && leverActivations[lever.lever_code]?.status !== 'paused' ? capacityCopy.lockedTooltip : undefined}
                        >
                          {activatingLeverId === lever.id ? 'ACTIVATING…' : 'REVIEW & ACTIVATE'}
                        </button>
                        )}
                        {/* AI robot icon removed (2026-08-01): AI entry lives in
                            the expanded details' Ask AI bar. */}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </RevealMore>
              </div>
            )}
          </section>

          {/* SECTION 2.5 — CUSTOMER SEGMENTS (vista free) */}
          {/* Va DESPUÉS de Revenue Levers: las tarjetas de la semana son primero.
              Free lo VE pero no puede activar (canActivate=false → candado).
              NOTA: aqui NO aplica el bypass de 'paused' de los levers —
              SegmentCounts no son plays de Levers y no tienen status por lever
              en leverActivations; su gate de cupo se mantiene tal cual. */}
          <section style={{ width: '100%', marginBottom: '48px' }}>
            <div className="rf-sec-divider-row">
              <span className="rf-sec-divider-label">CUSTOMER SEGMENTS</span>
              <div className="rf-sec-divider-rule"></div>
            </div>
            <SegmentCounts
              companyId={companyInfo?.company_id}
              token={token}
              canActivate={hasQuotaData ? canActivate : false}
              onUpgrade={() => onNavigate && onNavigate('pricing')}
            />
          </section>

          {/* SECTION 3 — SEGMENT OPTIMIZER */}
          <section style={{ width: '100%', marginBottom: '48px' }}>
            <div className="rf-sec-divider-row">
              <span className="rf-sec-divider-label">SEGMENT OPTIMIZER</span>
              <div className="rf-sec-divider-rule"></div>
            </div>

            <div className="rf-opt-card-redefined">
              <h4 className="rf-opt-title-text">SEGMENT OPTIMIZER</h4>
              <p className="rf-opt-subtitle-text">Tell us what you're planning and we'll find your best audience</p>

              <textarea
                className="rf-opt-textarea"
                placeholder="25% off on Allegro socks this Friday..."
                value={optimizerText}
                onChange={(e) => setOptimizerText(e.target.value)}
              />

              <div className="rf-opt-pills-row">
                <button className="rf-opt-pill-btn" onClick={() => handleApplyFastTag("25% off VIP Product Launch announcement socks Friday")}>
                  Product Launch
                </button>
                <button className="rf-opt-pill-btn" onClick={() => handleApplyFastTag("25% off checkout-clearing discount campaign this Friday")}>
                  Discount Campaign
                </button>
                <button className="rf-opt-pill-btn" onClick={() => handleApplyFastTag("Reactivation campaign sequence targeting inactive buyers with -25% checkouts")}>
                  Reactivation
                </button>
              </div>

              <button
                className="rf-opt-submit-btn"
                onClick={handleFindAudience}
              >
                FIND MY AUDIENCE →
              </button>
            </div>
          </section>

          {false && (
          /* SECTION 4 — RECOMMENDED ACTIONS */
          <section style={{ width: '100%', marginBottom: '48px' }}>
            <div className="rf-sec-divider-row">
              <span className="rf-sec-divider-label">RECOMMENDED ACTIONS</span>
              <div className="rf-sec-divider-rule"></div>
            </div>

            <div className="rf-actions-grid-redefined">
              {actionsList.map((action) => {
                let stripClass = "rf-action-left-strip";
                if (action.priority === 'HIGH PRIORITY') {
                  stripClass += " rf-left-strip-high";
                } else if (action.priority === 'CRITICAL') {
                  stripClass += " rf-left-strip-critical";
                } else {
                  stripClass += " rf-left-strip-medium";
                }

                return (
                  <div key={action.id} className="rf-action-row-redefined">
                    <div className={stripClass}></div>
                    <span className="rf-action-priority-label">{action.priority.replace(' PRIORITY', '')}</span>
                    <div className="rf-action-vertical-divider"></div>
                    <span className="rf-action-title-label">{action.title}</span>
                    <span className="rf-action-desc-label" title={action.description}>
                      – {action.description}
                    </span>
                    <span className="rf-action-val-label">{action.value}</span>
                    <button
                      className="rf-action-activate-btn"
                      onClick={() => {
                        setToastMessage(`Action activated: ${action.title} for cohort: ${action.segment}`);
                        setShowToast(true);
                        setTimeout(() => setShowToast(false), 3000);
                      }}
                    >
                      ACTIVATE
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
          )}

          {false && (
          /* SECTION 5 — REVENUE IMPACT */
          <section style={{ width: '100%', marginBottom: '48px' }}>
            <div className="rf-sec-divider-row">
              <span className="rf-sec-divider-label">REVENUE IMPACT</span>
              <div className="rf-sec-divider-rule"></div>
            </div>

            <div className="rf-impact-grid-redefined">
              <div className="rf-impact-left-redefined">
                <span className="rf-impact-heading-label">PERFORMANCE SNAPSHOT</span>
                <h2 className="rf-impact-main-pct rf-num-serif">{snapshotPct}%</h2>
                <p className="rf-impact-goal-subtext">of monthly goal</p>

                <div className="rf-impact-prog-track">
                  <div
                    className="rf-impact-prog-fill"
                    style={{ width: `${snapshotPct}%` }}
                  ></div>
                </div>

                <p className="rf-impact-recovered-big">{snapshotRecovered} recovered</p>
                <p className="rf-impact-projected-small">of {snapshotGoal} projected · {playsList.length} plays</p>
                <p className="rf-impact-trend-green">{snapshotTrend}</p>
                <p className="rf-impact-total-foot">Total with UPSELLErate: {totalRecovered}</p>
              </div>

              <div className="rf-impact-right-redefined">
                {playsList.map((play) => (
                  <div key={play.id} className="rf-play-row-redefined">
                    <span className="rf-play-name-label">{play.label}</span>
                    <div className="rf-play-desc-col">
                      <span className="rf-play-amount-label">{play.title}</span>
                      {play.recoveredText && (
                        <span className="rf-play-rate-label">{play.recoveredText}</span>
                      )}
                    </div>
                    <div className="rf-play-dot-container">
                      <div
                        className={`rf-play-status-dot ${play.isActive ? 'rf-play-status-dot-active rf-play-dot-pulsing' : 'rf-play-status-dot-done'}`}
                      />
                    </div>
                  </div>
                ))}

                <a
                  className="rf-history-footer-link"
                  onClick={() => {
                    setToastMessage("Full historic revenue log fetched successfully.");
                    setShowToast(true);
                    setTimeout(() => setShowToast(false), 2500);
                  }}
                >
                  View full history →
                </a>
              </div>
            </div>
          </section>
          )}

        </div>

        {false && (
        /* Sticky bottom bar footer */
        <footer className="rf-sticky-bar-footer-redefined">
          <div className="rf-sticky-left-box">
            <span className="rf-sticky-sparkle-icon">✦</span>
            <p className="rf-sticky-message-text">
              Activating the <span className="rf-sticky-highlight-text">'First-time Buyer'</span> play could cover 89% of your remaining weekly gap...
            </p>
          </div>

          <button className="rf-sticky-activate-btn" onClick={handleActivateStickyPlay}>
            ACTIVATE THIS PLAY →
          </button>
        </footer>
        )}

        {/* Confirmación previa a la activación en Klaviyo */}
        {confirmLever && (
          <div className="rf-confirm-overlay" onClick={() => setConfirmLever(null)}>
            <div className="rf-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="rf-confirm-title">Activate segment in Klaviyo</h3>
              <p className="rf-confirm-lever">
                {confirmLever.title} <span className="rf-confirm-value">{confirmLever.value}</span>
              </p>
              <p className="rf-confirm-desc">{confirmLever.description}</p>
              <div className="rf-confirm-box">
                <p className="rf-confirm-box-label">AUDIENCE</p>
                <p className="rf-confirm-box-text">
                  {confirmLever.segment}
                  {' — '}
                  <strong style={{ color: '#6d28d9' }}>
                    {confirmAudienceCount === null
                      ? 'counting customers…'
                      : confirmAudienceCount < 0
                        ? 'count unavailable'
                        : `${confirmAudienceCount.toLocaleString()} customers will be synced`}
                  </strong>
                </p>
                <p className="rf-confirm-box-label" style={{ marginTop: '10px' }}>WHAT WILL BE SYNCED</p>
                <ul className="rf-confirm-list">
                  <li>Customer profiles (email) matching this lever's audience</li>
                  <li>Segment membership property for a live Klaviyo segment</li>
                  <li>Top 3 recommended products per customer (ID, title, photo)</li>
                </ul>
                <p className="rf-confirm-note">
                  No campaign is created or sent — you design and launch the campaign in Klaviyo using this segment.
                </p>
              </div>
              <div className="rf-confirm-actions">
                <button className="rf-confirm-cancel" onClick={() => setConfirmLever(null)}>
                  Cancel
                </button>
                <button
                  className="rf-confirm-cancel"
                  style={{ borderColor: '#4f46e5', color: '#4f46e5', fontWeight: 700 }}
                  disabled={downloadingLever}
                  onClick={() => handleDownloadLeverCsv(confirmLever)}
                >
                  {downloadingLever ? 'Preparing…' : 'Download CSV'}
                </button>
                <button
                  className="rf-confirm-go"
                  onClick={() => handleReviewActivate(confirmLever)}
                >
                  CONFIRM & ACTIVATE
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmación para pausar la sincronización semanal */}
        {confirmStopLever && (
          <div className="rf-confirm-overlay" onClick={() => setConfirmStopLever(null)}>
            <div className="rf-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <h3 className="rf-confirm-title">Pause weekly sync?</h3>
              <p className="rf-confirm-desc">
                "{confirmStopLever.title}" will stop updating weekly. Its Klaviyo
                segment will empty out. You can reactivate it later.
              </p>
              <div className="rf-confirm-actions">
                <button className="rf-confirm-cancel" onClick={() => setConfirmStopLever(null)}>
                  Cancel
                </button>
                <button
                  className="rf-confirm-cancel"
                  style={{ borderColor: '#b91c1c', color: '#b91c1c', fontWeight: 700 }}
                  disabled={activatingLeverId === confirmStopLever.id}
                  onClick={() => handleStopSync(confirmStopLever)}
                >
                  PAUSE SYNC
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Segment Optimizer Chat Drawer */}
        <SegmentOptimizerChat
          key={segmentChatKey}
          isOpen={isSegmentChatOpen}
          onClose={() => setIsSegmentChatOpen(false)}
          onGoToLever={handleGoToLever}
          token={propToken}
          companyId={companyInfo?.company_id}
          initialContext={segmentChatContext}
          initialMessage={segmentChatInitialMessage}
          onViewPlans={() => onNavigate && onNavigate('pricing')}
        />

        {/* AI Drawer */}
        <div className={`rf-ai-drawer ${isAiPanelOpen ? 'rf-ai-drawer-active' : ''}`}>
          <div className="rf-ai-drawer-header">
            <h5 className="rf-ai-drawer-title">
              <span className="material-symbols-outlined rf-ai-drawer-title-icon">smart_toy</span>
              AI Assistant
            </h5>
            <button className="rf-btn-close-drawer" onClick={() => setIsAiPanelOpen(false)}>
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>

          <div className="rf-ai-drawer-body">
            <div className="rf-ai-panel-focus-card">
              <p className="rf-ai-panel-focus-tag">Current Focus</p>
              <h6 className="rf-ai-panel-focus-details">{aiFocus?.title} VIPs</h6>
            </div>

            <div className="rf-ai-panel-messages-stack">
              <p className="rf-ai-panel-message">
                I've analyzed this cohort relative to your {selectedCompany.toUpperCase()} database.
              </p>
              <p className="rf-ai-panel-message">
                The most effective move is a "Personalized Outreach" sequence starting with a thank-you note from your founder.
              </p>
              <p className="rf-ai-panel-message">
                Shall I draft the segment for your ESP?
              </p>
            </div>
          </div>

          <div className="rf-ai-drawer-footer">
            <button
              className="rf-btn-ai-submit-action"
              onClick={() => {
                setToastMessage(`Draft segment generated successfully for ${aiFocus?.title}!`);
                setShowToast(true);
                setTimeout(() => setShowToast(false), 3000);
                setIsAiPanelOpen(false);
              }}
            >
              Draft Segment
            </button>
          </div>
        </div>

        {/* Settings Modal */}
        {isSettingsOpen && (
          <div className="rf-settings-overlay">
            <div className="rf-settings-modal">
              <h4 className="rf-settings-title">Configure BigQuery Credentials</h4>
              <p className="rf-settings-description">
                Configure your Bearer Authentication Token to connect to FastAPI BigQuery endpoints at: <br/>
                <code>dashboard-dot-primeval-gear-417120.ue.r.appspot.com</code>
              </p>

              <div className="rf-settings-form-group">
                <label className="rf-settings-label">Target Company</label>
                <select
                  className="rf-settings-input"
                  value={tempCompany}
                  onChange={(e) => setTempCompany(e.target.value)}
                >
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className="rf-settings-form-group">
                <label className="rf-settings-label">Bearer Token</label>
                <input
                  type="password"
                  className="rf-settings-input"
                  placeholder="Paste Bearer Authorization Token..."
                  value={tempToken}
                  onChange={(e) => setTempToken(e.target.value)}
                />
              </div>

              <div className="rf-settings-footer-actions">
                <button className="rf-btn-settings-cancel" onClick={() => setIsSettingsOpen(false)}>
                  Cancel
                </button>
                <button className="rf-btn-settings-save" onClick={handleSaveSettings}>
                  Save & Validate
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
